import React from 'react';

import { Endpoint, Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { HPMSExpansibleRow } from '@philips-emr/hpms-stipulator-components';
import { FetchAPI, MapperAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';

import BaseController from '../BaseController';

interface ChangeRequest {
  value: ChangeRequestValue;
}

interface ChangeRequestValue {
  ieStatus: string;
  nrSeqProtocolo: number;
  nrProtocolo: number;
  dsMotivoRejeicao: string;
  descricao: string;
  cdPessoaFisica: string;
}

@Controller({ fragment: 'person-data-change-requests', component: 'change-request-layout' })
class ChangeRequestsController extends BaseController {

  @OnMount({ target: ['change-request-mapper'] })
  onMapperMount(e: MountEvent): void {
    const api = e.target as MapperAPI;

    api.addCustomMapping('readableAttributeName', this.generateGreatName);
  }  
  
  @OnBeforeFetch('person-data-change-requests-fetch')
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnMount('person-data-change-requests-fetch')
  onMount(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { cdPessoaFisica, nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    
    api.setParameters({ cdPessoaFisica, nrSeqBeneficiario });
    api.forceFetch();
  }

  generateGreatName(data: Record<string, Value>): Value {
    const resolvedAttributeName = new Map()
      .set('NM_PESSOA_FISICA', MessageService.translate('$expression.$744691'))
      .set('DT_NASCIMENTO', MessageService.translate('stipulator.data-nascimento'))
      .set('IE_ESTADO_CIVIL', MessageService.translate('$expression.$625399'))
      .set('NR_CPF', MessageService.translate('$expression.$286332'))
      .set('NR_CARTAO_NAC_SUS', MessageService.translate('$expression.$495468'))
      .set('DS_EMAIL', MessageService.translate('stipulator.email'))
      .set('NR_IDENTIDADE', MessageService.translate('$expression.$894444'))
      .set('DT_EMISSAO_CI', MessageService.translate('$expression.$759683'))
      .set('SG_EMISSORA_CI', MessageService.translate('$expression.$317603'))
      .set('NR_SEQ_PAIS', MessageService.translate('$expression.$295227'))
      .set('DS_ORGAO_EMISSOR_CI', MessageService.translate('$expression.$294884'))
      .set('NR_REG_GERAL_ESTRANG', MessageService.translate('$expression.$297894'))
      .set('IE_SEXO', MessageService.translate('$expression.$863406'))
      .set('CD_CEP', MessageService.translate('$expression.$883399'))
      .set('DS_ENDERECO', MessageService.translate('$expression.$289232'))
      .set('NR_ENDERECO', MessageService.translate('$expression.$294439'))
      .set('DS_COMPLEMENTO', MessageService.translate('$expression.$285555'))
      .set('DS_BAIRRO', MessageService.translate('$expression.$284200'))
      .set('DS_MUNICIPIO', MessageService.translate('$expression.$302837'))
      .set('SG_ESTADO', MessageService.translate('$expression.$289525'))
      .set('NR_DDI_TELEFONE', MessageService.translate('$expression.$303511'))
      .set('NR_DDD_TELEFONE', MessageService.translate('$expression.$287301'))
      .set('NR_TELEFONE', MessageService.translate('$expression.$299149=Telefone'))
      .set('NR_TELEFONE_CELULAR', MessageService.translate('$expression.$284755'))
      .set('CD_DECLARACAO_NASC_VIVO', MessageService.translate('$expression.$645216'))
      .set('NM_CONTATO', MessageService.translate('$expression.$1035437'))
      .set('NM_PAI', MessageService.translate('$expression.$1035439'))
      .set('IE_BOLETO_EMAIL', MessageService.translate('stipulator.email'))
      .set('NM_SOCIAL', MessageService.translate('$expression.$962558'));

    return resolvedAttributeName.get(data.attributeName);
  }

  @OnDynamicContentRender({ target: ['personDataChangeRequestsDatatable'] })
  onRender(event: RenderEvent): void {

    const { entry, data, render, type } = event;
    const params = { tasyFieldChangeRequestId: '', rejectReason: '', notesDescription: '', ieStatus: '', nrSeqProtocolo: '', ...data };
    const endpoint = Endpoint.GET.MOTIVO_REJEICAO;
    const nrSeqProtocolo = params.nrSeqProtocolo;
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const urlParams = { nrSeqBeneficiario, nrSeqProtocolo, nrSeqContrato };

    const divStyle = {
      transition: '0.10s ease-in-out',
      cursor: 'pointer',
    };

    if (type == 'row' && params.ieStatus == 'R') {
      render(() => <HPMSExpansibleRow params={urlParams} endpoint={endpoint}></HPMSExpansibleRow>);
    }

    if (entry === 'expandColumn' && params.ieStatus == 'R') {

      const renderLink: Function = () => {
        return (
          <div onClick={(e) => this.onExpandClick(e.target as HTMLDivElement)} className="c-menu-option__arrow" style={divStyle}></div>
        );
      };

      render(renderLink);
    }
  }

  onExpandClick = (dv: HTMLDivElement): void => {
    if (dv.style.transform) {
      dv.style.transform = '';
    } else {
      dv.style.transform = 'rotate(90deg)';
    }
  };

  @OnClick({ target: 'deck-data-change-request' })
  onCardClick(event: ChangeRequest): void {
    const { ieStatus, nrSeqProtocolo, cdPessoaFisica } = event.value;
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    if (ieStatus == 'R') {
      this.context.set({ nrSeqProtocolo, cdPessoaFisica });
      this.historyService.push(Route.ALTERACAO_CADASTRO, { nrSeqProtocolo, cdPessoaFisica, nrSeqGrupoContratoWeb, nrSeqContrato });

    }
  }
}

export default ChangeRequestsController;
